/**
 * AlgoQuant portfolio-engine eligibility. Shared by any feature that calls
 * `GET /portfolio-trees-v2/:draftSlug/exposure` or otherwise depends on the same engine payload rules.
 */
import { PortfolioType } from '@/types/portfolio';
import { WeightingTypeConstants } from '@/types/portfolio/AllocationWeightingConstants';
import type { IPortfolioTree, IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { isPortfolioTreeSubportfolio, isPortfolioTreeStrategy } from '@/types/IPortfolioTree';
import { findTreeComponent, flattenPortfolioTree } from '@/utils/portfolioTree';

/**
 * Whether the draft is eligible for the AlgoQuant portfolio-engine when analyzing
 * {@code portfolioTreeId} (root when omitted). Nested subportfolio selection still resolves against
 * the root’s type — callers pass the same tree id they use for exposure, metrics, or any other
 * engine-backed API.
 *
 * Equity baskets and equities-only QIS always use AlgoQuant (no admin-toggle check).
 * `containsOnlyEquities` is read from the draft root's apiConfig, not the node's, to preserve the
 * previous root-level behavior. Other types need `shouldUseAlgoQuant` (always true on BofA via
 * `useFeatureFlag`) and a root weighting of Manual or Custom. Pass the flag in so Vue computeds
 * track the Core toggle.
 */
export function getIsDraftEligibleForAlgoQuantPortfolioEngine({
  draft,
  itemUnderAnalysisPortfolioTreeId,
  shouldUseAlgoQuant,
}: {
  draft: IPortfolioTree | null;
  itemUnderAnalysisPortfolioTreeId: string | undefined;
  shouldUseAlgoQuant: boolean;
}): boolean {
  if (!draft) {
    return false;
  }

  const root = draft.portfolioTree;
  // purposefully fall back to `root.portfolioTreeId` if `itemUnderAnalysisPortfolioTreeId` is falsy
  const id = itemUnderAnalysisPortfolioTreeId || root.portfolioTreeId;
  let node: IPortfolioTreeSubportfolio | undefined;

  if (id === root.portfolioTreeId) {
    node = root;
  } else {
    const found = findTreeComponent(root, id);
    // Nested selection must resolve to a subportfolio, but eligibility always follows the root.
    node = found !== undefined && isPortfolioTreeSubportfolio(found) ? root : undefined;
  }

  if (!node) {
    return false;
  }

  if (node.type === PortfolioType.EQUITY_BASKET) {
    return true;
  }

  if (node.type === PortfolioType.QIS_BASKET && root.apiConfig?.containsOnlyEquities === true) {
    return true;
  }

  if (!shouldUseAlgoQuant) {
    return false;
  }

  const rootWeightingType = draft.allocation[root.portfolioTreeId]?.weighting.type;
  const hasManualOrCustomWeighting =
    rootWeightingType === WeightingTypeConstants.MANUAL || rootWeightingType === WeightingTypeConstants.CUSTOM;

  if (!hasManualOrCustomWeighting) {
    return false;
  }

  const flattenedTree = flattenPortfolioTree(draft.portfolioTree);

  // At least one leaf item must be an instrument to go through AQ
  // Temporarily until they handle non instruments items
  const hasInstrumentItems = flattenedTree.filter(isPortfolioTreeStrategy).some((x) => x.securityId != null);
  return hasInstrumentItems;
}
